/* eslint-disable react/forbid-prop-types */
import React from 'react';
import PropTypes from 'prop-types';
import {
  Row,
  Col,
  Form,
  Divider,
  Checkbox,
  Select,
} from 'antd';
import { useTranslation } from 'react-i18next';

const SoundMeterHD2SerieConfigReportFields = (props) => {
  const {
    soundMeter,
  } = props;

  const { t } = useTranslation();

  const reportParameters = React.useMemo(() => {
    const result = [];
    for (let i = 0; i < soundMeter.availableReportParameterCount; i += 1) {
      result.push(i);
    }
    return result;
  }, [soundMeter]);

  return (
    <Row>
      <Col span={24}>
        <Divider>
          {t('soundMeterHD2Series.components.configForm.reportDivider')}
        </Divider>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              label={t('soundMeterHD2Series.components.configForm.reportActive')}
              name="reportActive"
              valuePropName="checked"
            >
              <Checkbox />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              noStyle
              dependencies={['reportActive']}
            >
              {({ getFieldValue }) => (
                <Form.Item
                  label={t('soundMeterHD2Series.components.configForm.reportInterval')}
                  name={['report', 'interval']}
                  rules={[{ required: getFieldValue('reportActive'), message: t('validations.required') }]}
                  initialValue={soundMeter.availableReportTimes.length === 1
                    ? soundMeter.availableReportTimes[0] : null}
                >
                  <Select
                    disabled={!getFieldValue('reportActive')}
                    optionLabelProp="label"
                    options={
                      soundMeter.availableReportTimes.map((reportTime) => ({
                        label: t(`enums.soundMeters.times.${reportTime}`),
                        value: reportTime,
                      }))
                    }
                    filterOption={(input, option) => {
                      const result = option.value.toLowerCase().includes(input.toLowerCase());
                      return result;
                    }}
                  />
                </Form.Item>
              )}
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          noStyle
          dependencies={['reportActive']}
        >
          {({ getFieldValue }) => (
            <>
              <Row gutter={24}>
                {reportParameters.map((reportParameter) => (
                  <>
                    <Col span={12}>
                      <Form.Item
                        label={t('soundMeterHD2Series.components.configForm.reportParameter', { index: reportParameter + 1 })}
                        name={['report', 'parameters', reportParameter, 'parameter']}
                        rules={[{ required: getFieldValue('reportActive'), message: t('validations.required') }]}
                        initialValue={soundMeter.availableReportParameters.length === 1
                          ? soundMeter.availableReportParameters[0] : null}
                      >
                        <Select
                          disabled={!getFieldValue('reportActive')}
                          optionLabelProp="label"
                          options={
                            soundMeter.availableReportParameters.map((parameter) => ({
                              label: t(`enums.soundMeters.parameters.${parameter}`),
                              value: parameter,
                            }))
                          }
                          filterOption={(input, option) => {
                            const result = option.value.toLowerCase().includes(input.toLowerCase());
                            return result;
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        noStyle
                        dependencies={[['report', 'parameters', reportParameter, 'parameter']]}
                      >
                        {({ getFieldValue: getFieldValuePond }) => {
                          const parameter = getFieldValuePond(['report', 'parameters', reportParameter, 'parameter']);
                          if (!parameter) {
                            return null;
                          }
                          const availablePonds = soundMeter.availableReportParameterPonds
                            .filter((p) => p.parameter === parameter)
                            .map((p) => p.pond);
                          if (!availablePonds.length) {
                            return null;
                          }
                          return (
                            <Form.Item
                              label={t('soundMeterHD2Series.components.configForm.reportPond', { index: reportParameter + 1 })}
                              name={['report', 'parameters', reportParameter, 'pond']}
                              rules={[
                                {
                                  required: getFieldValue('reportActive'),
                                  message: t('validations.required'),
                                },
                                () => ({
                                  validator(_, value) {
                                    if (!availablePonds.includes(value)) {
                                      const str = availablePonds.join(', ');
                                      return Promise.reject(new Error(t('validations.notInList', { items: str })));
                                    }
                                    return Promise.resolve();
                                  },
                                }),
                              ]}
                              initialValue={availablePonds.length === 1
                                ? availablePonds[0] : null}
                            >
                              <Select
                                disabled={!getFieldValue('reportActive')}
                                optionLabelProp="label"
                                options={
                                  availablePonds.map((pond) => ({
                                    label: t(`enums.soundMeters.ponds.${pond}`),
                                    value: pond,
                                  }))
                                }
                                filterOption={(input, option) => {
                                  const result = option.value
                                    .toLowerCase().includes(input.toLowerCase());
                                  return result;
                                }}
                              />
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    </Col>
                  </>
                ))}
              </Row>
              {(soundMeter.canReportOctave || soundMeter.canReportThirdOctave) && (
                <Row gutter={24}>
                  {soundMeter.canReportOctave && (
                    <Col span={12}>
                      <Form.Item
                        label={t('soundMeterHD2Series.components.configForm.reportOctave')}
                        name={['report', 'octave']}
                        valuePropName="checked"
                      >
                        <Checkbox
                          disabled={!getFieldValue('reportActive')}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {soundMeter.canReportThirdOctave && (
                    <Col span={12}>
                      <Form.Item
                        label={t('soundMeterHD2Series.components.configForm.reportThirdOctave')}
                        name={['report', 'thirdOctave']}
                        valuePropName="checked"
                      >
                        <Checkbox
                          disabled={!getFieldValue('reportActive')}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              )}
              {soundMeter.canReportStatistics && (
                <>
                  <Row gutter={24}>
                    <Col span={24}>
                      <Form.Item
                        label={t('soundMeterHD2Series.components.configForm.reportStatistics')}
                        name={['report', 'statisticsActive']}
                        valuePropName="checked"
                      >
                        <Checkbox
                          disabled={!getFieldValue('reportActive')}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item
                    noStyle
                    dependencies={[['report', 'statisticsActive']]}
                  >
                    {({ getFieldValue: getFieldValueStatisticsActive }) => (
                      <Row gutter={24}>
                        <Col span={12}>
                          <Form.Item
                            label={t('soundMeterHD2Series.components.configForm.reportStatisticsParamenter')}
                            name={['report', 'statistics', 'parameter']}
                            rules={[{ required: getFieldValue('reportActive') && getFieldValueStatisticsActive(['report', 'statisticsActive']), message: t('validations.required') }]}
                            initialValue={soundMeter.availableStatisticsParameters.length === 1
                              ? soundMeter.availableStatisticsParameters[0] : null}
                          >
                            <Select
                              disabled={!getFieldValue('reportActive') || !getFieldValueStatisticsActive(['report', 'statisticsActive'])}
                              optionLabelProp="label"
                              options={
                                soundMeter.availableStatisticsParameters.map((parameter) => ({
                                  label: t(`enums.soundMeters.parameters.${parameter}`),
                                  value: parameter,
                                }))
                              }
                              filterOption={(input, option) => {
                                const result = option.value.toLowerCase()
                                  .includes(input.toLowerCase());
                                return result;
                              }}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            noStyle
                            dependencies={[['report', 'statistics', 'parameter']]}
                          >
                            {({ getFieldValue: getFieldValuePond }) => {
                              const parameter = getFieldValuePond(['report', 'statistics', 'parameter']);
                              if (!parameter) {
                                return null;
                              }
                              const availablePonds = soundMeter.availableStatisticsParametersPonds
                                .filter((p) => p.parameter === parameter)
                                .map((p) => p.pond);
                              if (!availablePonds.length) {
                                return null;
                              }
                              return (
                                <Form.Item
                                  label={t('soundMeterHD2Series.components.configForm.reportStatisticsPond')}
                                  name={['report', 'statistics', 'pond']}
                                  rules={[
                                    {
                                      required: getFieldValue('reportActive') && !getFieldValueStatisticsActive([['report', 'statisticsActive']]),
                                      message: t('validations.required'),
                                    },
                                    () => ({
                                      validator(_, value) {
                                        if (!availablePonds.includes(value)) {
                                          const str = availablePonds.join(', ');
                                          return Promise.reject(new Error(t('validations.notInList', { items: str })));
                                        }
                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                  initialValue={availablePonds.length === 1
                                    ? availablePonds[0] : null}
                                >
                                  <Select
                                    disabled={!getFieldValue('reportActive') || !getFieldValueStatisticsActive(['report', 'statisticsActive'])}
                                    optionLabelProp="label"
                                    options={
                                      availablePonds.map((pond) => ({
                                        label: t(`enums.soundMeters.ponds.${pond}`),
                                        value: pond,
                                      }))
                                    }
                                    filterOption={(input, option) => {
                                      const result = option.value
                                        .toLowerCase().includes(input.toLowerCase());
                                      return result;
                                    }}
                                  />
                                </Form.Item>
                              );
                            }}
                          </Form.Item>
                        </Col>
                      </Row>
                    )}
                  </Form.Item>
                </>
              )}
            </>
          )}
        </Form.Item>
      </Col>
    </Row>
  );
};

const propTypes = {
  soundMeter: PropTypes.object.isRequired,
};

SoundMeterHD2SerieConfigReportFields.propTypes = propTypes;

export default SoundMeterHD2SerieConfigReportFields;
